import { sitesApi } from "#/api/sitesApi";
import {
  showErrorNotification,
  showSuccessNotification,
} from "#/components/notifications";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { usePermission } from "#/hooks/usePermission";
import type { Site } from "#/types/sites";
import { logger } from "#/utils/logger";
import { Alert, Button, List, Loader, Modal } from "@mantine/core";
import { IconAlertTriangle, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

interface DeleteSiteModalProps {
  opened: boolean;
  onClose: () => void;
  site: Site | undefined;
  onSuccess: () => void;
}

const DeleteSiteModal = ({
  opened,
  onClose,
  site,
  onSuccess,
}: DeleteSiteModalProps) => {
  const { t } = useTranslation();
  const { canDelete } = usePermission();

  const { data: preview, isPending: previewPending } = useQuery({
    queryKey: ["site-delete-preview", site?.id],
    queryFn: async () => {
      if (!site) return null;
      const res = await sitesApi.getDeletePreview(site.id);
      return res.data;
    },
    enabled: opened && Boolean(site),
    staleTime: 0,
    gcTime: 0,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      return await sitesApi.delete(id);
    },
    onSuccess: (res) => {
      const wasArchived = res.data?.archived === true;
      showSuccessNotification({
        message: wasArchived
          ? t("sites.delete.archiveSuccess", "Site archived successfully")
          : t("sites.delete.deleteSuccess", "Site deleted successfully"),
      });
      onClose();
      onSuccess();
    },
    onError: (error) => {
      logger.error("DeleteSiteModal delete error:", error);
      showErrorNotification({
        message: t("sites.delete.error", "Failed to delete site"),
      });
    },
  });

  const handleConfirm = () => {
    if (site) deleteMutation.mutate(site.id);
  };

  const willArchive = preview?.will_archive === true;
  const counts = preview?.counts;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      title={
        willArchive
          ? t("sites.delete.archiveTitle", "Archive site")
          : t("sites.delete.deleteTitle", "Delete site")
      }
      closeOnClickOutside={false}
    >
      {previewPending || !preview ? (
        <div className="flex items-center justify-center py-8">
          <Loader size="sm" />
        </div>
      ) : willArchive ? (
        <>
          <Alert
            icon={<IconAlertTriangle size={18} />}
            color="yellow"
            className="mb-3"
          >
            {t(
              "sites.delete.archiveExplanation",
              "This site is in use and will be archived (soft-deleted). Items, vehicles, templates and devices currently assigned to this site will be unlinked. Historical scan records are preserved. Archived sites cannot be restored."
            )}
          </Alert>
          <p className="mb-2">
            <strong>{site?.name}</strong>{" "}
            {t("sites.delete.linkedTo", "is linked to:")}
          </p>
          <List size="sm" className="mb-3">
            {counts && counts.items > 0 && (
              <List.Item>
                {counts.items} {t("sites.delete.items", "item(s)")}
              </List.Item>
            )}
            {counts && counts.vehicles > 0 && (
              <List.Item>
                {counts.vehicles} {t("sites.delete.vehicles", "vehicle(s)")}
              </List.Item>
            )}
            {counts && counts.item_templates > 0 && (
              <List.Item>
                {counts.item_templates}{" "}
                {t("sites.delete.templates", "template(s)")}
              </List.Item>
            )}
            {counts && counts.devices > 0 && (
              <List.Item>
                {counts.devices} {t("sites.delete.devices", "device(s)")}
              </List.Item>
            )}
            {counts && counts.scan_history > 0 && (
              <List.Item>
                {counts.scan_history}{" "}
                {t("sites.delete.scanHistory", "scan history entries")}
              </List.Item>
            )}
          </List>
        </>
      ) : (
        <p>
          {t(
            "sites.delete.hardDeleteConfirmation",
            "Are you sure you want to permanently delete"
          )}{" "}
          <strong>{site?.name}</strong>?
        </p>
      )}

      <div className="flex items-center justify-end gap-2 mt-4">
        <Button
          variant="outline"
          color="black"
          onClick={onClose}
          disabled={deleteMutation.isPending}
        >
          {t("common.cancel")}
        </Button>
        <PermissionTooltip disabled={!canDelete()}>
          <Button
            color="red"
            leftSection={<IconTrash size={18} />}
            onClick={handleConfirm}
            loading={deleteMutation.isPending}
            disabled={!canDelete() || previewPending || !preview}
          >
            {willArchive
              ? t("sites.delete.archiveButton", "Archive")
              : t("common.delete")}
          </Button>
        </PermissionTooltip>
      </div>
    </Modal>
  );
};

export default DeleteSiteModal;
